import { useCallback, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Boxes,
  ChevronRight,
  ChevronsUpDown,
  Inbox,
  KeyRound,
  LogOut,
  Megaphone,
  Plus,
  ScrollText,
  Settings,
  ShieldCheck,
  Target,
  Users,
  Landmark,
  Wallet,
} from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useMe } from "@/lib/permissions";
import { useCrumbTrail } from "@/components/app/crumbs";
import { ProviderSwitch } from "@/components/app/ProviderSwitch";
import { UniversalSearch } from "@/components/app/UniversalSearch";
import { ExportProvider } from "@/components/app/exports";
import { LumosLogo } from "@/components/brand/LumosLogo";
import { TransactionSheet } from "@/components/app/TransactionSheet";

/** `permission` may list several: the item shows when the role has any of them. */
type NavItem = { to: string; label: string; icon: ReactNode; permission: string | string[]; end?: boolean };

const NAV: { label?: string; items: NavItem[] }[] = [
  {
    label: "Статистика",
    items: [
      { to: "/", label: "Статистика", icon: <BarChart3 />, permission: "stats.view", end: true },
      { to: "/pnl", label: "P&L", icon: <Landmark />, permission: "pnl.view" },
    ],
  },
  {
    label: "Работа",
    items: [
      { to: "/clients", label: "Клиенты", icon: <Users />, permission: "clients.view" },
      { to: "/accounts", label: "Кабинеты", icon: <Boxes />, permission: "accounts.view" },
      { to: "/campaigns", label: "Кампании", icon: <Target />, permission: "accounts.view" },
    ],
  },
  {
    label: "Операции",
    items: [
      { to: "/broadcasts", label: "Рассылки", icon: <Megaphone />, permission: "broadcast.send" },
      { to: "/app-requests", label: "Заявки", icon: <Inbox />, permission: "apprequests.view" },
    ],
  },
  {
    label: "Система",
    items: [
      { to: "/sources", label: "Аккаунты", icon: <KeyRound />, permission: "accounts.credentials" },
      { to: "/logs", label: "Логи", icon: <ScrollText />, permission: "accounts.view" },
      { to: "/wallets", label: "Обозреватель блокчейна", icon: <Wallet />, permission: "wallets.view" },
      {
        to: "/settings",
        label: "Настройки",
        icon: <Settings />,
        // Its tabs have their own rights: bots and wallets can be granted without the rest.
        permission: ["settings.view", "wallets.view", "bots.view", "mcp.use"],
      },
      { to: "/access", label: "Доступы", icon: <ShieldCheck />, permission: "users.manage" },
    ],
  },
];

function Brand() {
  return (
    // The wordmark is the way home from anywhere, the way every other app's is.
    // `/` is the stats page; it is also the fallback route, so this never 404s.
    <Link
      to="/"
      aria-label="Статистика"
      className="flex h-10 items-center rounded-md px-1.5 outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      {/* Full wordmark when open; the glowing mark alone on the 52px rail. */}
      <div className="grid min-w-0 gap-1 group-data-[state=collapsed]/sidebar:hidden">
        <LumosLogo className="h-5 w-auto" />
        <span className="truncate text-[11px] text-[var(--sidebar-foreground)]/75">Maxima</span>
      </div>
      <LumosLogo mark className="hidden size-8 group-data-[state=collapsed]/sidebar:block" />
    </Link>
  );
}

export function AppShell({ children, onLogout }: { children: ReactNode; onLogout: () => void }) {
  const { me, can } = useMe();
  const trail = useCrumbTrail();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(false);
  const openTransaction = useCallback(() => setTransaction(true), []);

  const name = me?.email ?? "";

  return (
    <SidebarProvider className="h-dvh min-h-0">
      <Sidebar>
        {/* The kit pads the rail only at the sides; top and bottom are ours. */}
        <SidebarHeader className="pt-3">
          <Brand />
          <ProviderSwitch />
          <UniversalSearch onTransaction={openTransaction} />
        </SidebarHeader>

        <SidebarContent>
          <nav aria-label="Навигация" className="flex flex-col gap-1">
            {NAV.map((group) => {
              const items = group.items.filter((i) =>
                Array.isArray(i.permission) ? i.permission.some((p) => can(p)) : can(i.permission),
              );
              if (items.length === 0) return null;
              return (
                <SidebarGroup key={group.label}>
                  {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
                  <SidebarMenu>
                    {items.map((item) => {
                      const active = item.end ? pathname === item.to : pathname.startsWith(item.to);
                      return (
                        <SidebarMenuItem key={item.to}>
                          {/* Not `asChild` with a link: the row renders its
                              icon and label beside the child, and a Slot takes
                              exactly one element. */}
                          <SidebarMenuButton
                            isActive={active}
                            icon={item.icon}
                            tooltip={item.label}
                            onClick={() => navigate(item.to)}
                          >
                            {item.label}
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroup>
              );
            })}
          </nav>
        </SidebarContent>

        <SidebarFooter className="pb-3">
          <SidebarMenu>
            <SidebarMenuItem>
              {/* The one account control: who is signed in, and the way out. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    icon={<Avatar size="xs" name={name || "?"} />}
                    tooltip={name}
                    trailing={<ChevronsUpDown className="size-3.5 opacity-60" />}
                    className="group-data-[state=collapsed]/sidebar:justify-center group-data-[state=collapsed]/sidebar:px-0"
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{name}</span>
                      <span className="block truncate text-[11px] text-[var(--sidebar-foreground)]/75">
                        {me?.role?.name ?? "—"}
                      </span>
                    </span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-60">
                  <DropdownMenuLabel>
                    <span className="block truncate text-sm font-medium">{name}</span>
                    <span className="text-muted-foreground block truncate text-xs">{me?.role?.name ?? "—"}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onLogout}>
                    <LogOut />
                    Выйти
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="border-border flex h-14 shrink-0 items-center gap-2 border-b px-3 md:px-4">
          <SidebarTrigger />

          {/* The trail takes what is left and truncates; the controls beside it
              keep their own width. Nowrap, or a long trail wraps underneath
              the search button instead of shortening. */}
          <Breadcrumb className="w-auto min-w-0 flex-1 overflow-hidden">
            <BreadcrumbList className="flex-nowrap">
              {trail.map((crumb, index) => {
                const last = index === trail.length - 1;
                return (
                  <BreadcrumbItem key={`${crumb.label}-${index}`} className={last ? "min-w-0" : "shrink-0"}>
                    {last || !crumb.to ? (
                      <BreadcrumbPage className="truncate">{crumb.label}</BreadcrumbPage>
                    ) : (
                      <>
                        <BreadcrumbLink asChild>
                          <Link to={crumb.to}>{crumb.label}</Link>
                        </BreadcrumbLink>
                        <BreadcrumbSeparator>
                          <ChevronRight />
                        </BreadcrumbSeparator>
                      </>
                    )}
                  </BreadcrumbItem>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>

          {can("clients.topup") && (
            <Button size="sm" onClick={() => setTransaction(true)}>
              <Plus />
              <span className="max-sm:hidden">Транзакция</span>
            </Button>
          )}
        </header>

        {/* Exports register while their component is mounted, so the menu follows the page and tab. */}
        <ExportProvider>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </ExportProvider>
      </SidebarInset>

      <TransactionSheet open={transaction} onOpenChange={setTransaction} />
    </SidebarProvider>
  );
}
